import { I18n } from "@/i18n/I18n";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useShallow } from "zustand/react/shallow";
import { SearchList } from "../SearchList";
import { PartType } from "@/backend/access/part/types";
import { Button } from "react-bootstrap";
import { PartParams } from "@/backend/access/part/types";
import { PartsTable } from "./PartsTable";

interface PartInstancesListProps {
  projectId: string;
  onCreateClick: () => void;
  onDelete: (part: PartParams) => void;
}

export function PartInstancesList({ 
  projectId, 
  onCreateClick, 
  onDelete,
}: PartInstancesListProps) {
  const navigate = useNavigate();
  const projectParts = useProjectStore(useShallow(state => 
    projectId ? state.projectParts.filter(p => p.projectId === projectId) : []
  ));

  const partInstances = useMemo(() => {
    return projectParts.filter(part => part.type === PartType.PART_INSTANCE);
  }, [projectParts]);

  const partInstancesWithTitle = useMemo(() => {
    return partInstances.map(part => ({
      ...part,
      title: part.title
    }));
  }, [partInstances]);

  const [filteredList, setFilteredList] = useState<PartParams[]>(partInstances);

  useEffect(() => {
    setFilteredList(partInstances);
  }, [partInstances]);

  const handleSetItems = useCallback((items: (PartParams & { title: string })[]) => {
    setFilteredList(items as PartParams[]);
  }, []);

  const handleViewClick = useCallback((partId: string) => {
    void navigate(`/parts/${partId}`);
  }, [navigate]);

  return (
    <>
      <div className="mb-3">
        <Button 
          variant="primary" 
          size="sm" 
          onClick={onCreateClick}
        >
          {I18n.get('createPartInstance')}
        </Button>
      </div>
      <SearchList 
        items={partInstancesWithTitle} 
        setItems={handleSetItems} 
        modelLabel={I18n.get('part')}
      />
      <PartsTable 
        parts={filteredList}
        onDelete={onDelete}
        onView={handleViewClick}
      />
    </>
  );
}
